import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Phone, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  LANDING_SECTION_IDS,
  SALES_PHONE,
  SALES_PHONE_DISPLAY,
  scrollToSection,
} from './constants';

const PROOF_ITEMS = ['Leads', 'Events', 'Directory', 'Member engagement'];

interface HeroSectionProps {
  isAuthenticated: boolean;
}

export function HeroSection({ isAuthenticated }: HeroSectionProps) {
  return (
    <section className="relative py-20 lg:py-28 overflow-hidden">
      <div className="container px-6 mx-auto flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl space-y-8"
        >
          <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 border border-primary/20 text-xs font-semibold text-primary uppercase tracking-wider">
            <Sparkles className="h-3 w-3" />
            <span>The Community Operating System</span>
          </div>

          <h1 className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-black tracking-tighter text-foreground leading-[1.1]">
            Run your community. <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary via-primary/80 to-primary/60">
              Capture leads. Grow revenue.
            </span>
          </h1>

          <p className="max-w-2xl mx-auto text-muted-foreground text-lg sm:text-xl leading-relaxed">
            Milora is the all-in-one platform for community owners to manage members, publish
            updates, run events, showcase businesses, and turn engagement into measurable outcomes.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <Link to={isAuthenticated ? '/dashboard' : '/login'}>
              <Button
                size="lg"
                className="h-14 px-8 rounded-full text-lg font-semibold shadow-xl shadow-primary/20 hover:scale-105 transition-all"
              >
                {isAuthenticated ? 'Go to Dashboard' : 'Get Started'}
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Button
              size="lg"
              variant="outline"
              className="h-14 px-8 rounded-full text-lg font-semibold hover:bg-muted/50 transition-all"
              onClick={() => scrollToSection(LANDING_SECTION_IDS.contact)}
            >
              Request Demo
            </Button>
          </div>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 text-sm text-muted-foreground">
            <a
              href={`tel:${SALES_PHONE}`}
              className="inline-flex items-center gap-2 font-medium text-foreground hover:text-primary transition-colors"
            >
              <Phone className="h-4 w-4 text-primary" />
              {SALES_PHONE_DISPLAY}
            </a>
            <span className="hidden sm:inline text-border">|</span>
            <span>Talk to our team about your community</span>
          </div>

          <div className="flex flex-wrap items-center justify-center gap-3 pt-4">
            {PROOF_ITEMS.map((item) => (
              <span
                key={item}
                className="rounded-full border border-border/50 bg-muted/40 px-4 py-1.5 text-xs font-semibold text-muted-foreground"
              >
                {item}
              </span>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}
